import { AppTab } from '../components/tab/tab';
import { Suspense, useMemo, useState } from 'react';
import { GraphModel } from '../graph/graph.model';
import {
  ModelScope,
  ReadonlyWatchable,
  TargetableModel,
  useInScopeModel,
  useWatchableValue,
  Watchable,
} from '../actions/framework';
import { DG } from '@cotera/sdk/core';
import { TabLink, useTabParams } from '../tab-manager';
import { useSuspenseQueries } from '@tanstack/react-query';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useTenantedQueryKey } from '@cotera/client/app/hooks/use-tenanted-query-key';
import { ErrorBoundary } from 'react-error-boundary';
import {
  KanbanCard,
  KanbanCards,
  KanbanHeader,
  KanbanBoard,
  KanbanProvider,
  KanbanItemProps,
} from '../components/ui/kanban';
import { ATCStackFrameStatus } from '@cotera/api';
import { RunOrGroupStats } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { CardDescription, CardTitle } from '../components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { joinElements } from '@cotera/client/app/components/utils';
import { DisplayError, toast, Tooltip } from '@cotera/client/app/components';
import { Skeleton } from '../components/ui/skeleton';
import { useStackFramesAtPrefix } from '../hooks/use-stackframes-at-prefix';
import { RUN_STATUS_MAP } from './utils';
import { getStatusIndicatorClassName } from '../lib/utils';
import { makeBreadCrumbs } from './breadcrumbs.utils';
import { useRetryStackframe } from '@cotera/client/app/hooks/use-retry-stackframe';
import { Button } from '../components/ui/button';

const PAGE_SIZE = 50;

class RunHistoryModel implements TargetableModel {
  readonly id: string;
  readonly changes: ReadonlyWatchable<Record<string, string | boolean>>;
  readonly pageTitle: ReadonlyWatchable<string>;

  constructor(
    readonly params: {
      graph: ReadonlyWatchable<DG.BuildExplorer>;
      key: string;
    }
  ) {
    this.id = `run-history-${params.key}`;
    this.changes = Watchable.fromValue({});
    this.pageTitle = Watchable.fromValue('Operations Monitoring');
  }

  isTargeted = false;
  target = () => {
    this.isTargeted = true;
  };
  markAsNotTargeted = () => {
    this.isTargeted = false;
  };
}

export const RunHistoryPage = () => {
  const { runKey } = useTabParams<{ runKey: string }>();

  const graphModel = useInScopeModel(GraphModel);
  const model = useMemo(
    () =>
      new RunHistoryModel({
        graph: graphModel.graph,
        key: runKey,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graphModel.graph]
  );

  // Create skeleton items for loading state
  const skeletonItems: KanbanItem[] = [
    {
      id: 'skeleton-1',
      name: 'Loading...',
      column: 'pending',
      type: 'run',
      startAt: new Date(),
      endAt: null,
      url: '/runs/skeleton-1',
    },
    {
      id: 'skeleton-2',
      name: 'Loading...',
      column: 'pending',
      type: 'run',
      startAt: new Date(),
      endAt: null,
      url: '/runs/skeleton-2',
    },
    {
      id: 'skeleton-3',
      name: 'Loading...',
      column: 'failed',
      type: 'run',
      startAt: new Date(),
      endAt: null,
      url: '/runs/skeleton-3',
    },
    {
      id: 'skeleton-4',
      name: 'Loading...',
      column: 'completed',
      type: 'run',
      startAt: new Date(),
      endAt: null,
      url: '/runs/skeleton-4',
    },
  ];

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <div className="flex flex-col gap-4 flex-1 h-full">
          <ErrorBoundary
            fallbackRender={({ error }) => <DisplayError error={error} />}
          >
            <Suspense
              fallback={
                <Board
                  type="run"
                  runKey={runKey}
                  items={skeletonItems}
                  totals={{ pending: 2, failed: 1, completed: 1 }}
                  getMore={async () => []}
                  children={(feature) => (
                    <KanbanCard
                      column={feature.column}
                      id={feature.id}
                      key={feature.id}
                      name={feature.name}
                    >
                      <div className="flex items-start justify-between gap-2 w-full">
                        <div className="flex flex-col gap-1 w-full">
                          <div className="flex items-center justify-between gap-2 w-full">
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                        </div>
                      </div>
                      <Skeleton className="h-4 w-24" />
                    </KanbanCard>
                  )}
                />
              }
            >
              <SuspensefulRunHistory runKey={runKey} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </ModelScope>
    </AppTab>
  );
};

const useStackFramesForPrefix = (prefix: string) => {
  const client = useTenantedClient();
  const tenantedKey = useTenantedQueryKey(['stack-frames', prefix]);

  const get = async (
    status: 'pending' | 'failed' | 'completed',
    page: number
  ) => {
    return Assert.assertOk(
      await client.atc.runGroup.forPrefix({
        prefix,
        status,
        page,
        perPage: PAGE_SIZE,
      })
    );
  };

  const [{ data: pending }, { data: failed }, { data: completed }] =
    useSuspenseQueries({
      queries: [
        {
          queryKey: [...tenantedKey, 'pending'],
          queryFn: () => get('pending', 0),
        },
        {
          queryKey: [...tenantedKey, 'failed'],
          queryFn: () => get('failed', 0),
        },
        {
          queryKey: [...tenantedKey, 'completed'],
          queryFn: () => get('completed', 0),
        },
      ],
    });

  return {
    pending,
    failed,
    completed,
    getMore: get,
  };
};

const SuspensefulRunHistory = ({ runKey }: { runKey: string }) => {
  const stackFrames = useStackFramesAtPrefix(runKey);

  const {
    pending,
    failed,
    completed,
    getMore: getMoreStackFrames,
  } = useStackFramesForPrefix(runKey);

  const stats =
    stackFrames.type === 'group'
      ? stackFrames.stats
      : {
          //TODO this isnt right - neeed to get the stats about the children
          pending: pending.length,
          failed: failed.length,
          completed: completed.length,
        };

  const getMore = async (
    page: number,
    status: 'pending' | 'failed' | 'completed'
  ) => {
    switch (status) {
      case 'pending':
        return convertToKanbanItems(await getMoreStackFrames('pending', page));
      case 'failed':
        return convertToKanbanItems(await getMoreStackFrames('failed', page));
      case 'completed':
        return convertToKanbanItems(
          await getMoreStackFrames('completed', page)
        );
    }
  };

  const items = [
    ...convertToKanbanItems(pending),
    ...convertToKanbanItems(failed),
    ...convertToKanbanItems(completed),
  ];

  const childrenType = items.length > 0 ? items[0]!.type : 'run';

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        <Board
          type={childrenType}
          runKey={runKey}
          items={items}
          totals={stats}
          getMore={getMore}
        />
      </div>
    </div>
  );
};

type KanbanItem = KanbanItemProps & {
  url: string;
  type: 'run' | 'group';
  startAt: Date | string | null;
  endAt: Date | string | null;
  stats?: { pending: number; failed: number; completed: number };
};

const convertToKanbanItems = (frames: RunOrGroupStats[]): KanbanItem[] => {
  return frames.map((frame) => {
    if (frame.type === 'run') {
      return {
        type: 'run',
        id: frame.runKey + '|' + frame.frameKey,
        url: `/run/${frame.runKey}/${frame.frameKey}`,
        name: frame.runKey,
        column: frame.status,
        startAt: frame.startAt,
        endAt: frame.endAt,
      };
    }

    return {
      type: 'group',
      url: `/runs/${frame.runKey}`,
      id: frame.runKey,
      name: frame.runKey,
      column: frame.status,
      startAt: frame.stats.startAt,
      endAt: frame.stats.endAt,
      stats: frame.stats,
    };
  });
};

const dateFormatter = (date: Date) => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const Board = ({
  items,
  totals,
  type,
  getMore,
  runKey,
  children,
}: {
  type: 'run' | 'group';
  items: KanbanItem[];
  totals: { pending: number; failed: number; completed: number };
  getMore: (
    page: number,
    status: 'pending' | 'failed' | 'completed'
  ) => Promise<KanbanItem[]>;
  runKey: string;
  children?: (feature: KanbanItem) => React.ReactNode;
}) => {
  const { mutate: retryRun } = useRetryStackframe(runKey, () => {
    toast.success('Run retried - reload window to see changes');
  });
  const [features, setFeatures] = useState(items);
  const graphModel = useInScopeModel(GraphModel);
  const baseGraph = useWatchableValue(graphModel.graph);

  const loadedCounts = {
    pending: features.filter((item) => item.column === 'pending').length,
    failed: features.filter((item) => item.column === 'failed').length,
    completed: features.filter((item) => item.column === 'completed').length,
  };

  const path = makeBreadCrumbs(runKey, baseGraph);

  // Check which columns are empty
  const emptyColumns = RUN_STATUS_MAP.filter((column) => {
    const columnId = column.id as keyof typeof loadedCounts;
    return loadedCounts[columnId] === 0;
  });

  // Check if all columns are empty
  const allColumnsEmpty = emptyColumns.length === RUN_STATUS_MAP.length;

  /*
   * warning: this is going to be v slow due to the baseGraph.at calls
   * we do this to accurately get the name of a rel or col for a given point in time
   */
  const makeTitle = (item: KanbanItem) => {
    const parts = item.name.split('|');
    const root = parts[0] ?? '';
    const rest = parts.slice(1);

    if (root === 'workflow-program-row' && rest.length > 0) {
      if (rest.length > 2) {
        //must be a "run"
        return rest.at(-1);
      }

      // Handle scheduled agents - these use a constant 'scheduled_agents' instead of a rel identity
      if (rest[0] === 'scheduled_agents') {
        if (rest.length === 2) {
          const agentIdentity = DG.Graph.Identity.fromString(rest[1]!);
          const agent = baseGraph
            .at(agentIdentity)
            .getAgentByIdentity({ agentIdentity });
          if (agent) {
            return agent.symbol;
          }
        }
        return 'Scheduled Agents';
      }

      const relIdentity = DG.Graph.Identity.fromString(rest[0]!);
      const rel = baseGraph
        .at(relIdentity)
        .getBuiltRelByIdentity({ relIdentity });
      if (rel) {
        if (rest.length === 2) {
          const colIdentity = DG.Graph.Identity.fromString(rest[1]!);
          const col = baseGraph
            .at(colIdentity)
            .getBuiltColumnByIdentity({ relIdentity, colIdentity });
          if (col) {
            return `${col.symbol}`;
          }
        }
        return rel.symbol;
      }
    }

    return item.name;
  };

  // If all columns are empty, show default behavior
  if (allColumnsEmpty) {
    return (
      <div className="h-full py-4 flex flex-col gap-2">
        <Breadcrumb>
          <BreadcrumbList>
            {joinElements(
              path.map((crumb, index) => {
                if (index === path.length - 1) {
                  return (
                    <BreadcrumbItem key={index}>
                      <BreadcrumbPage>{crumb.display}</BreadcrumbPage>
                    </BreadcrumbItem>
                  );
                }

                return (
                  <BreadcrumbItem key={index}>
                    <BreadcrumbLink asChild>
                      {crumb.url !== undefined ? (
                        <TabLink to={crumb.url} type="replace">
                          {crumb.display}
                        </TabLink>
                      ) : (
                        <span>{crumb.display}</span>
                      )}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                );
              }),
              (i) => (
                <BreadcrumbSeparator key={i} />
              )
            )}
          </BreadcrumbList>
        </Breadcrumb>
        <KanbanProvider
          columns={RUN_STATUS_MAP}
          data={features}
          className="h-[calc(100%-1.5rem)]"
          disableDragging
        >
          {(column) => {
            const hasMore =
              totals[column.id as keyof typeof totals] >
              loadedCounts[column.id as keyof typeof loadedCounts];

            return (
              <KanbanBoard id={column.id} key={column.id}>
                <KanbanHeader>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${getStatusIndicatorClassName(
                          column.id as ATCStackFrameStatus
                        )}`}
                      />
                      <span>{column.name}</span>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {totals[column.id as keyof typeof totals]}
                    </span>
                  </div>
                </KanbanHeader>
                <KanbanCards
                  id={column.id}
                  itemHeight={type === 'group' ? 100 : 80}
                  hasMore={hasMore}
                  onLoadMore={async () => {
                    const more = await getMore(
                      loadedCounts[column.id as keyof typeof loadedCounts] /
                        PAGE_SIZE,
                      column.id as 'pending' | 'failed' | 'completed'
                    );
                    setFeatures([...features, ...more]);
                  }}
                >
                  {children ||
                    ((feature: KanbanItem) => (
                      <KanbanCard
                        column={column.id}
                        id={feature.id}
                        key={feature.id}
                        name={feature.name}
                      >
                        <div className="flex items-start justify-between gap-2 w-full">
                          <div className="flex flex-col gap-1 w-full">
                            <div className="flex items-center justify-between gap-2 w-full">
                              <Tooltip side="top" tooltipContent={feature.name}>
                                <TabLink
                                  asChild
                                  to={feature.url}
                                  type="replace"
                                >
                                  <CardTitle className="cursor-pointer">
                                    {makeTitle(feature)}
                                  </CardTitle>
                                </TabLink>
                              </Tooltip>
                              <CardDescription>{feature.type}</CardDescription>
                            </div>
                          </div>
                        </div>
                        <p className="m-0 text-muted-foreground text-xs">
                          {feature.startAt !== null
                            ? dateFormatter(new Date(feature.startAt))
                            : 'Pending'}{' '}
                          -{' '}
                          {feature.endAt !== null
                            ? dateFormatter(new Date(feature.endAt))
                            : 'Pending'}
                        </p>
                      </KanbanCard>
                    ))}
                </KanbanCards>
              </KanbanBoard>
            );
          }}
        </KanbanProvider>
      </div>
    );
  }

  // If some columns have items, show collapsed view with gutter
  return (
    <div className="h-full py-4 flex flex-col gap-2">
      <Breadcrumb>
        <BreadcrumbList>
          {joinElements(
            path.map((crumb, index) => {
              if (index === path.length - 1) {
                return (
                  <BreadcrumbItem key={index}>
                    <BreadcrumbPage>{crumb.display}</BreadcrumbPage>
                  </BreadcrumbItem>
                );
              }

              return (
                <BreadcrumbItem key={index}>
                  <BreadcrumbLink asChild>
                    {crumb.url !== undefined ? (
                      <TabLink to={crumb.url} type="replace">
                        {crumb.display}
                      </TabLink>
                    ) : (
                      <span>{crumb.display}</span>
                    )}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              );
            }),
            (i) => (
              <BreadcrumbSeparator key={i} />
            )
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex justify-end mb-4">
        <Button variant="outline" className="w-fit" onClick={() => retryRun()}>
          Retry All Failures
        </Button>
      </div>

      <div className="flex h-[calc(100%-1.5rem)]">
        {/* Left Gutter for Empty Columns */}
        {emptyColumns.length > 0 && (
          <div className="w-64 border-r border-border pr-4">
            <div className="space-y-2">
              {emptyColumns.map((column) => (
                <div
                  key={column.id}
                  className="p-3 border border-border rounded-lg bg-muted/30"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${getStatusIndicatorClassName(
                          column.id as ATCStackFrameStatus
                        )}`}
                      />
                      <span className="text-sm font-medium">{column.name}</span>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {totals[column.id as keyof typeof totals]}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    No items
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Kanban Board for Columns with Items */}
        <div className="flex-1 pl-4">
          <KanbanProvider
            columns={RUN_STATUS_MAP.filter((column) => {
              const columnId = column.id as keyof typeof loadedCounts;
              return loadedCounts[columnId] > 0;
            })}
            data={features}
            className="h-full"
            disableDragging
          >
            {(column) => {
              const hasMore =
                totals[column.id as keyof typeof totals] >
                loadedCounts[column.id as keyof typeof loadedCounts];

              return (
                <KanbanBoard id={column.id} key={column.id}>
                  <KanbanHeader>
                    <div className="flex items-center justify-between gap-2 pb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-2 w-2 rounded-full ${getStatusIndicatorClassName(
                            column.id as ATCStackFrameStatus
                          )}`}
                        />
                        <span>{column.name}</span>
                      </div>
                      <span className="text-muted-foreground text-xs bg-secondary rounded-sm px-2 py-1">
                        {totals[column.id as keyof typeof totals]}
                      </span>
                    </div>
                  </KanbanHeader>
                  <KanbanCards
                    id={column.id}
                    itemHeight={type === 'group' ? 100 : 80}
                    hasMore={hasMore}
                    onLoadMore={async () => {
                      const more = await getMore(
                        loadedCounts[column.id as keyof typeof loadedCounts] /
                          PAGE_SIZE,
                        column.id as 'pending' | 'failed' | 'completed'
                      );
                      setFeatures([...features, ...more]);
                    }}
                  >
                    {children ||
                      ((feature: KanbanItem) => (
                        <KanbanCard
                          column={column.id}
                          id={feature.id}
                          key={feature.id}
                          name={feature.name}
                        >
                          <div className="flex items-start justify-between gap-2 w-full">
                            <div className="flex flex-col gap-1 w-full">
                              <div className="flex items-center justify-between gap-2 w-full">
                                <Tooltip
                                  side="top"
                                  tooltipContent={feature.name}
                                >
                                  <TabLink
                                    asChild
                                    to={feature.url}
                                    type={
                                      feature.type === 'run'
                                        ? 'push'
                                        : 'replace'
                                    }
                                  >
                                    <CardTitle className="cursor-pointer">
                                      {makeTitle(feature)}
                                    </CardTitle>
                                  </TabLink>
                                </Tooltip>
                                <CardDescription>
                                  {feature.type}
                                </CardDescription>
                              </div>
                            </div>
                          </div>
                          <p className="m-0 text-muted-foreground text-xs">
                            {feature.startAt !== null
                              ? dateFormatter(new Date(feature.startAt))
                              : 'Pending'}{' '}
                            -{' '}
                            {feature.endAt !== null
                              ? dateFormatter(new Date(feature.endAt))
                              : 'Pending'}
                          </p>
                        </KanbanCard>
                      ))}
                  </KanbanCards>
                </KanbanBoard>
              );
            }}
          </KanbanProvider>
        </div>
      </div>
    </div>
  );
};
